import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { siteInfo } from '../data/mock';
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube, Download, BookOpen } from 'lucide-react';
import CatalogueModal from './CatalogueModal';
import { useSiteSettings } from '../hooks/useSiteSettings';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showCatalogueModal, setShowCatalogueModal] = useState(false);
  const settings = useSiteSettings();

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div>
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_tentopia-events/artifacts/6vzdco9s_WhatsApp%20Image%202026-02-27%20at%2018.45.00%20%281%29.jpeg" 
                alt="Barnum Événement" 
                className="h-20 w-20 rounded-full border-2 border-orange-500/30"
                style={{ objectFit: 'cover', objectPosition: 'center', transform: 'scale(1.15)', clipPath: 'circle(50%)' }}
              />
            </div>
            <p className="text-gray-400 text-sm mb-6">
              Spécialiste de la location de matériel événementiel en Normandie depuis 2015.
            </p>
            <div className="flex gap-3">
              <a href="https://www.facebook.com/share/1EDXJ5mPLi/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/barnum_evenement?stkn=czR4MmVlMmtlbW9p" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/share/1EDXJ5mPLi/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Catalogue */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-orange-400">Catalogue</h4>
            <ul className="space-y-3">
              <li><Link to="/catalogue/barnums-tentes" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Barnum et Tentes de Réception</Link></li>
              <li><Link to="/catalogue/mobilier-deco" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Mobilier et Vaisselle</Link></li>
              <li><Link to="/catalogue/materiel-accessoires" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Matériel et Accessoires</Link></li>
              <li><Link to="/catalogue/gonflables-jeux" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Gonflables et Jeux</Link></li>
              <li><Link to="/catalogue/restauration" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Restauration et Buvette</Link></li>
              <li><Link to="/catalogue/pack-tout-inclus" className="text-sm text-orange-400 font-medium hover:text-orange-300 transition-colors">PACK TOUT INCLUS</Link></li>
            </ul>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-orange-400">Navigation</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Accueil</Link></li>
              <li><Link to="/catalogue" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Notre Catalogue</Link></li>
              <li><Link to="/realisations" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Nos Réalisations</Link></li>
              <li><Link to="/faq" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">FAQ</Link></li>
              <li><Link to="/a-propos" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">À Propos</Link></li>
              <li><Link to="/blog" className="text-sm text-gray-400 hover:text-orange-400 transition-colors" data-testid="footer-blog-link">Conseils Événementiels</Link></li>
              <li><Link to="/guide-technique" className="text-sm text-gray-400 hover:text-orange-400 transition-colors" data-testid="footer-guide-link">Guide technique &amp; sécurité</Link></li>
              <li><Link to="/devis" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Demande de Devis</Link></li>
              <li className="pt-3 border-t border-gray-700"><Link to="/mentions-legales" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Mentions Légales</Link></li>
              <li><Link to="/confidentialite" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Politique de Confidentialité</Link></li>
              <li><Link to="/conditions-generales-location" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">Conditions Générales de Location</Link></li>
            </ul>
          </div>

          {/* Téléchargements */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-orange-400">Téléchargements</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={settings.catalogueUrlWithoutPrices}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-400 hover:text-orange-400 transition-colors flex items-center gap-2"
                  data-testid="footer-feuilleter-catalogue"
                >
                  <BookOpen className="w-4 h-4" />
                  Feuilleter le catalogue
                </a>
              </li>
              <li>
                <button 
                  onClick={() => setShowCatalogueModal(true)}
                  className="text-sm text-gray-400 hover:text-orange-400 transition-colors flex items-center gap-2 cursor-pointer"
                  data-testid="footer-telecharger-catalogue"
                >
                  <Download className="w-4 h-4" />
                  Télécharger le Catalogue 2026 (avec les tarifs)
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-orange-400">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-orange-400 flex-shrink-0" />
                <span className="text-sm text-gray-400">Gainneville</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-orange-400 flex-shrink-0" />
                <a href="tel:0698430992" className="text-sm text-gray-400 hover:text-orange-400">Mehdi - 06 98 43 09 92</a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-orange-400 flex-shrink-0" />
                <span className="text-sm text-gray-400">Cauville-sur-Mer</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-orange-400 flex-shrink-0" />
                <a href="tel:0623893298" className="text-sm text-gray-400 hover:text-orange-400">Damien - 06 23 89 32 98</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-orange-400 flex-shrink-0" />
                <a href={`mailto:${siteInfo.email}`} className="text-sm text-gray-400 hover:text-orange-400">{siteInfo.email}</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* SEO Local Links */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h5 className="text-sm font-semibold text-gray-400 mb-4">Nos zones d'intervention en Normandie</h5>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <Link to="/location/le-havre" className="text-xs text-gray-500 hover:text-orange-400 transition-colors">Location Le Havre</Link>
            <Link to="/location/etretat" className="text-xs text-gray-500 hover:text-orange-400 transition-colors">Location Étretat</Link>
            <Link to="/location/gonfreville" className="text-xs text-gray-500 hover:text-orange-400 transition-colors">Location Gonfreville</Link>
            <Link to="/location/honfleur" className="text-xs text-gray-500 hover:text-orange-400 transition-colors">Location Honfleur</Link>
            <Link to="/location/rouen" className="text-xs text-gray-500 hover:text-orange-400 transition-colors">Location Rouen</Link>
            <Link to="/location/dieppe" className="text-xs text-gray-500 hover:text-orange-400 transition-colors">Location Dieppe</Link>
            <Link to="/location/caen" className="text-xs text-gray-500 hover:text-orange-400 transition-colors">Location Caen</Link>
            <Link to="/location/fecamp" className="text-xs text-gray-500 hover:text-orange-400 transition-colors">Location Fécamp</Link>
            <Link to="/location/deauville" className="text-xs text-gray-500 hover:text-orange-400 transition-colors">Location Deauville</Link>
            <Link to="/location/lisieux" className="text-xs text-gray-500 hover:text-orange-400 transition-colors">Location Lisieux</Link>
            <Link to="/location/gainneville" className="text-xs text-gray-500 hover:text-orange-400 transition-colors">Location Gainneville</Link>
          </div>
          <h5 className="text-sm font-semibold text-gray-400 mt-8 mb-4">Nos guides pratiques</h5>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <Link to="/blog/taille-barnum-mariage-100-personnes" className="text-xs text-gray-500 hover:text-orange-400 transition-colors" data-testid="footer-article-1">Quelle taille de barnum pour 100 personnes ?</Link>
            <Link to="/blog/prix-location-barnum-normandie" className="text-xs text-gray-500 hover:text-orange-400 transition-colors" data-testid="footer-article-2">Prix de location d'un barnum en Normandie</Link>
            <Link to="/blog/barnum-ou-chapiteau-que-choisir" className="text-xs text-gray-500 hover:text-orange-400 transition-colors" data-testid="footer-article-3">Barnum ou chapiteau : lequel choisir ?</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              © {currentYear} Barnum Événement. Tous droits réservés.
            </p>
            <div className="flex gap-6">
              <Link to="/mentions-legales" className="text-xs text-gray-500 hover:text-orange-400">Mentions légales</Link>
              <Link to="/confidentialite" className="text-xs text-gray-500 hover:text-orange-400">Politique de confidentialité</Link>
              <Link to="/conditions-generales-location" className="text-xs text-gray-500 hover:text-orange-400">Conditions Générales de Location</Link>
            </div>
          </div>
        </div>
      </div>
      {/* Catalogue Modal for lead capture */}
      <CatalogueModal isOpen={showCatalogueModal} onClose={() => setShowCatalogueModal(false)} />
    </footer>
  );
};

export default Footer;
